import { useCallback } from "react";
import { channelTag, readNotificationPreferences, shouldSkipInPageNotification, showInPageNotification } from "../../lib/notifications";

export type NotifyArgs = {
  voidId: string;
  channelId: string;
  channelName?: string;
  authorId?: string;
  authorName?: string;
  content?: string;
  messageId?: string;
  pushSent?: boolean;
  isMine?: boolean;
  isCurrentChannel: boolean;
  mutedSpaces: Record<string, boolean>;
  hiddenSpaces: Record<string, boolean>;
  favorites: string[];
  addPreview: (fid: string, preview: { id: string; content: string; authorName?: string; createdAt?: string }) => void;
  refreshPreview: (fid: string) => void;
  markUnread: (fid: string) => void;
  onClickChannel: (voidId: string, channelId: string) => void;
  notifiedRef: React.MutableRefObject<Record<string, boolean>>;
  seenIdsRef: React.MutableRefObject<Set<string>>;
  setNotified?: (fid: string) => void;
};

/**
 * Centralizes in-page notification handling for message:new events.
 */
export function useInPageNotification() {
  const handleNotification = useCallback((args: NotifyArgs) => {
    const {
      voidId,
      channelId,
      channelName,
      authorName,
      content,
      messageId,
      pushSent,
      isMine,
      isCurrentChannel,
      mutedSpaces,
      hiddenSpaces,
      favorites,
      addPreview,
      refreshPreview,
      markUnread,
      onClickChannel,
      notifiedRef,
      seenIdsRef,
      setNotified,
    } = args;

    if (messageId) {
      const seen = seenIdsRef.current;
      if (seen.has(messageId)) return;
      seen.add(messageId);
      if (seen.size > 5000) {
        seenIdsRef.current = new Set(Array.from(seen).slice(-1000));
      }
    }

    const fid = channelId.includes(":") ? channelId : `${voidId}:${channelId}`;
    const isDirectMessage = String(voidId).startsWith("dm_");

    // If actively viewing this channel, skip the notification + unread bump.
    // Use visibility only as the "is the user looking at this" signal — NOT
    // document.hasFocus(), which returns false in the Android WebView even when
    // the page is visible and frontmost. Including it meant that on mobile an
    // incoming message in the channel you were actively reading still bumped its
    // unread count, leaving the space's dot lit after you'd seen the message.
    const activelyViewing = isCurrentChannel && document.visibilityState === "visible";
    if (activelyViewing) {
      return;
    }

    if (!isMine) {
      markUnread(fid);
    }

    const preview = {
      id: messageId || String(Date.now()),
      content: content || "",
      authorName,
      createdAt: new Date().toISOString(),
    };
    addPreview(fid, preview);
    try {
      if (favorites.includes(fid)) refreshPreview(fid);
    } catch {
      /* noop */
    }

    if (isMine) return;
    if (mutedSpaces[voidId] || hiddenSpaces[voidId]) return;
    const settings = readNotificationPreferences();
    if (settings.globalMute) return;
    if (settings.perChannelDefault === "none") return;
    if (settings.perChannelDefault === "mentions") {
      const keys = loadMentionKeys();
      if (!isMention(content || "", keys)) return;
    }
    if (!isDirectMessage && notifiedRef.current[fid]) return;
    if (!isDirectMessage) {
      notifiedRef.current[fid] = true;
      if (setNotified) setNotified(fid);
    }

    try {
      const inBackground = document.visibilityState === "hidden" || !document.hasFocus();
      const skipInPage = shouldSkipInPageNotification({
        pushSent,
        visibility: document.visibilityState,
        hasFocus: document.hasFocus(),
      });

      if (settings.soundEnabled && inBackground && !skipInPage) {
        let toneUrl = settings.toneUrl;
        if (!toneUrl) {
          const stored = localStorage.getItem("user");
          let u: any = null;
          try {
            if (stored) u = JSON.parse(stored);
          } catch {
            /* noop */
          }
          toneUrl = u?.toneUrl || null;
        }
        if (toneUrl) new Audio(toneUrl).play().catch(() => {});
      }

      if (settings.desktopEnabled && "Notification" in window && inBackground && !skipInPage) {
        const title = isDirectMessage ? `DM from ${authorName || "User"}` : channelName ? `#${channelName}` : `#${channelId}`;
        const body = `${authorName || "User"}: ${(content || "").slice(0, 80)}`;
        const tag = isDirectMessage && messageId ? `${channelTag(voidId, channelId)}:${messageId}` : channelTag(voidId, channelId);
        showInPageNotification(title, body, tag, () => onClickChannel(voidId, channelId), channelTag(voidId, channelId));
      }
    } catch {
      /* noop */
    }
  }, []);

  return { handleNotification };
}

function loadMentionKeys(): string[] {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    const keys = [parsed?.username, parsed?.name, parsed?.displayName]
      .filter((k) => typeof k === "string" && k.trim().length > 0)
      .map((k) => String(k).trim().toLowerCase());
    return Array.from(new Set(keys));
  } catch {
    return [];
  }
}

function isMention(content: string, keys: string[]): boolean {
  if (hasMentionToken(content, "all")) return true;
  if (keys.length === 0) return false;
  return keys.some((key) => hasMentionToken(content, key));
}

function hasMentionToken(content: string, token: string): boolean {
  const escaped = String(token || "").trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (!escaped) return false;
  return new RegExp(`(^|\\s)@${escaped}(?![A-Za-z0-9_.-])`, "i").test(String(content || ""));
}
